import React from "react";
import { Box, Container, Heading, Text, VStack, HStack, SimpleGrid, Button } from "@chakra-ui/react";
import { FaHome, FaSeedling, FaRocket, FaClock, FaChartLine, FaShieldAlt, FaLeaf } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const SolutionsWealthBuilding: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solutions/wealth-building"
        fallbackTitle="Wealth Building Solutions"
        fallbackDescription="Build sustainable wealth through integrated solar, finance, and property strategies"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Heading size="2xl" fontWeight="bold">
              Wealth Building Solutions
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">
              Build long-term wealth through property and solar investments
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Wealth Building Pyramid Visual Concept */}
      <Container maxW="container.xl" py={16}>
        <Heading size="lg" mb={12} textAlign="center">
          The Wealth Building Pyramid
        </Heading>
        <VStack spacing={0} align="stretch">
          {/* Level 3: Accelerate */}
          <Box
            bg={brandConfig.colors.primary}
            color="white"
            p={8}
            borderTopRadius="xl"
            maxW="400px"
            mx="auto"
            w="100%"
            textAlign="center"
          >
            <HStack justify="center" mb={3}>
              <Box as={FaRocket} size="28px" />
              <Heading size="md">Level 3: Accelerate</Heading>
            </HStack>
            <Text fontSize="sm">Multiple income streams, advanced tax strategies, wealth preservation</Text>
          </Box>

          {/* Level 2: Growth */}
          <Box
            bg={brandConfig.colors.primary}
            color="white"
            p={8}
            maxW="600px"
            mx="auto"
            w="100%"
            textAlign="center"
          >
            <HStack justify="center" mb={3}>
              <Box as={FaSeedling} size="28px" />
              <Heading size="md">Level 2: Growth</Heading>
            </HStack>
            <Text fontSize="sm">Investment properties, solar portfolio expansion, equity building</Text>
          </Box>

          {/* Level 1: Foundation */}
          <Box
            bg="#C2A878"
            color={brandConfig.colors.primary}
            p={8}
            borderBottomRadius="xl"
            maxW="800px"
            mx="auto"
            w="100%"
            textAlign="center"
          >
            <HStack justify="center" mb={3}>
              <Box as={FaHome} size="28px" />
              <Heading size="md">Level 1: Foundation</Heading>
            </HStack>
            <Text fontSize="sm">First home ownership, solar installation, cash flow optimization</Text>
          </Box>
        </VStack>
      </Container>

      {/* Timeline showing wealth progression */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <Heading size="lg" mb={12} textAlign="center">
            Your Wealth Journey Timeline
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
            <Box bg="white" p={6} borderRadius="lg" border="2px solid" borderColor={brandConfig.colors.primary} textAlign="center">
              <Text fontSize="2xl" fontWeight="bold" color={brandConfig.colors.primary} mb={2}>Year 1</Text>
              <VStack align="stretch" spacing={2}>
                <Text fontWeight="bold" fontSize="lg">Foundation Set</Text>
                <Text fontSize="sm" color="gray.600">First home purchased</Text>
                <Text fontSize="sm" color="gray.600">Solar installed</Text>
                <Text fontSize="sm" color="gray.600">$200/month savings</Text>
              </VStack>
            </Box>

            <Box bg="white" p={6} borderRadius="lg" border="2px solid" borderColor={brandConfig.colors.primary} textAlign="center">
              <Text fontSize="2xl" fontWeight="bold" color={brandConfig.colors.primary} mb={2}>Year 3</Text>
              <VStack align="stretch" spacing={2}>
                <Text fontWeight="bold" fontSize="lg">Equity Building</Text>
                <Text fontSize="sm" color="gray.600">$50k equity gained</Text>
                <Text fontSize="sm" color="gray.600">Energy costs near zero</Text>
                <Text fontSize="sm" color="gray.600">Refinance ready</Text>
              </VStack>
            </Box>

            <Box bg="white" p={6} borderRadius="lg" border="2px solid" borderColor={brandConfig.colors.primary} textAlign="center">
              <Text fontSize="2xl" fontWeight="bold" color={brandConfig.colors.primary} mb={2}>Year 5</Text>
              <VStack align="stretch" spacing={2}>
                <Text fontWeight="bold" fontSize="lg">Growth Phase</Text>
                <Text fontSize="sm" color="gray.600">Investment property</Text>
                <Text fontSize="sm" color="gray.600">Second solar system</Text>
                <Text fontSize="sm" color="gray.600">Passive income started</Text>
              </VStack>
            </Box>

            <Box bg="white" p={6} borderRadius="lg" border="2px solid" borderColor="#C2A878" textAlign="center">
              <Text fontSize="2xl" fontWeight="bold" color={brandConfig.colors.primary} mb={2}>Year 10</Text>
              <VStack align="stretch" spacing={2}>
                <Text fontWeight="bold" fontSize="lg">Acceleration</Text>
                <Text fontSize="sm" color="gray.600">Portfolio of 3+ properties</Text>
                <Text fontSize="sm" color="gray.600">Solar on all assets</Text>
                <Text fontSize="sm" color="gray.600">Financial independence</Text>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Strategy Cards */}
      <Container maxW="container.xl" py={16}>
        <Heading size="lg" mb={12} textAlign="center">
          4 Core Wealth Building Strategies
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          <Box bg="white" p={8} border="1px solid" borderColor="gray.200" borderRadius="xl" _hover={{ borderColor: brandConfig.colors.primary, boxShadow: "lg" }} transition="all 0.3s">
            <HStack mb={4}>
              <Box as={FaHome} size="32px" color={brandConfig.colors.primary} />
              <Heading size="md">Property Equity Growth</Heading>
            </HStack>
            <Text color="gray.700" mb={4}>
              Build equity through strategic property purchases with built-in solar assets that reduce holding costs and increase property value.
            </Text>
            <Text fontSize="sm" color="gray.600" fontStyle="italic">
              Average 7-10% annual appreciation plus solar value addition
            </Text>
          </Box>

          <Box bg="white" p={8} border="1px solid" borderColor="gray.200" borderRadius="xl" _hover={{ borderColor: brandConfig.colors.primary, boxShadow: "lg" }} transition="all 0.3s">
            <HStack mb={4}>
              <Box as={FaLeaf} size="32px" color={brandConfig.colors.primary} />
              <Heading size="md">Solar Cash Flow</Heading>
            </HStack>
            <Text color="gray.700" mb={4}>
              Generate immediate positive cash flow by eliminating or drastically reducing energy costs across all your properties.
            </Text>
            <Text fontSize="sm" color="gray.600" fontStyle="italic">
              Typical savings: $2,000-$5,000 per property annually
            </Text>
          </Box>

          <Box bg="white" p={8} border="1px solid" borderColor="gray.200" borderRadius="xl" _hover={{ borderColor: brandConfig.colors.primary, boxShadow: "lg" }} transition="all 0.3s">
            <HStack mb={4}>
              <Box as={FaShieldAlt} size="32px" color={brandConfig.colors.primary} />
              <Heading size="md">Tax Optimization</Heading>
            </HStack>
            <Text color="gray.700" mb={4}>
              Maximize deductions through depreciation schedules, solar tax credits, and strategic refinancing to keep more of what you earn.
            </Text>
            <Text fontSize="sm" color="gray.600" fontStyle="italic">
              Potential tax savings: 30% federal solar credit + state incentives
            </Text>
          </Box>

          <Box bg="white" p={8} border="1px solid" borderColor="gray.200" borderRadius="xl" _hover={{ borderColor: brandConfig.colors.primary, boxShadow: "lg" }} transition="all 0.3s">
            <HStack mb={4}>
              <Box as={FaChartLine} size="32px" color={brandConfig.colors.primary} />
              <Heading size="md">Compound Growth</Heading>
            </HStack>
            <Text color="gray.700" mb={4}>
              Leverage increased equity to acquire additional properties, creating a compounding effect that accelerates wealth accumulation.
            </Text>
            <Text fontSize="sm" color="gray.600" fontStyle="italic">
              Reinvest savings into new acquisitions every 2-3 years
            </Text>
          </Box>
        </SimpleGrid>
      </Container>

      {/* CTA Section */}
      <Box bg={brandConfig.colors.primary} py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center" color="white">
            <Box as={FaClock} size="48px" />
            <Heading size="xl">Start Building Wealth Today</Heading>
            <Text fontSize="lg" maxW="3xl" opacity={0.9}>
              The best time to start was yesterday. The second best time is now. Book a free wealth building consultation to create your personalized strategy.
            </Text>
            <HStack spacing={4}>
              <Button size="lg" bg="#C2A878" color={brandConfig.colors.primary} _hover={{ bg: "#b09060" }} fontWeight="bold" onClick={() => navigate("/contact")}>
                Book Free Consultation
              </Button>
              <Button size="lg" variant="outline" borderColor="white" color="white" _hover={{ bg: "whiteAlpha.200" }}>
                Download Wealth Guide
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default SolutionsWealthBuilding;
